<template>
  <BaseModal
    :is-open="isOpen"
    :title="resolvedTitle"
    :description="resolvedMessage"
    size="sm"
    @close="handleCancel"
  >
    <div class="flex items-center gap-3">
      <div class="flex-shrink-0">
        <div class="w-10 h-10 rounded-full flex items-center justify-center" :class="iconBgClass">
          <component :is="iconComponent" class="w-5 h-5 text-white" />
        </div>
      </div>
      <div class="flex-1">
        <p class="text-sm text-gray-600">{{ resolvedMessage }}</p>
        <p v-if="detail" class="text-xs text-gray-500 mt-1">{{ detail }}</p>
      </div>
    </div>

    <template #footer>
      <BaseButton variant="outline" @click="handleCancel" :disabled="loading">
        {{ resolvedCancelText }}
      </BaseButton>
      <BaseButton
        :variant="confirmVariant"
        @click="handleConfirm"
        :loading="loading"
        :loading-text="resolvedLoadingText"
      >
        {{ resolvedConfirmText }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseModal from './BaseModal.vue'
import BaseButton from './BaseButton.vue'
import ExclamationTriangleIcon from '@heroicons/vue/24/solid/ExclamationTriangleIcon'
import InformationCircleIcon from '@heroicons/vue/24/solid/InformationCircleIcon'
import CheckCircleIcon from '@heroicons/vue/24/solid/CheckCircleIcon'

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Konfirmasi',
  },
  message: {
    type: String,
    default: '',
  },
  detail: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: 'warning',
    validator: (value: string) => ['warning', 'danger', 'info', 'success'].includes(value),
  },
  confirmText: {
    type: String,
    default: 'Ya, Lanjutkan',
  },
  cancelText: {
    type: String,
    default: 'Batal',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingText: {
    type: String,
    default: 'Memproses...',
  },
})

const { t } = useI18n()

const resolvedTitle = computed(() => props.title || t('confirmDialog.title'))
const resolvedMessage = computed(() => props.message || t('confirmDialog.message'))
const resolvedConfirmText = computed(() => props.confirmText || t('confirmDialog.confirm'))
const resolvedCancelText = computed(() => props.cancelText || t('confirmDialog.cancel'))
const resolvedLoadingText = computed(() => props.loadingText || t('confirmDialog.loading'))

const emit = defineEmits<{
  (_e: 'confirm'): void
  (_e: 'cancel'): void
}>()

const iconBgClass = computed(() => {
  const classes = {
    warning: 'bg-yellow-500',
    danger: 'bg-red-500',
    info: 'bg-blue-500',
    success: 'bg-green-500',
  }
  return (classes as Record<string, string>)[props.type]
})

const confirmVariant = computed(() => {
  type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'success'
  const variants: Record<string, ButtonVariant> = {
    warning: 'secondary',
    danger: 'danger',
    info: 'primary',
    success: 'success',
  }
  return variants[props.type] ?? 'secondary'
})

const iconComponent = computed(() => {
  switch (props.type) {
    case 'warning':
    case 'danger':
      return ExclamationTriangleIcon
    case 'info':
      return InformationCircleIcon
    case 'success':
      return CheckCircleIcon
    default:
      return ExclamationTriangleIcon
  }
})

function handleConfirm() {
  emit('confirm')
}

function handleCancel() {
  emit('cancel')
}
</script>
